import classnames from 'classnames';
import React, { useEffect, useState } from 'react';

import { getPayloadObject, getTelemetryAttributes, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { PriceComponent } from '../components/price.component';
import { IActionContext, IComponent, IComponentProps, IGridSettings, IImageSettings, TelemetryEvent } from '@msdyn365-commerce/core';
import { getCartState, ICartActionResult, ICartState } from '@msdyn365-commerce/global-state';
import {
    Address,
    AttributeValue,
    Customer,
    ProductAvailableQuantity,
    ProductDeliveryOptions,
    ProductDimension,
    ProductPrice,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import { ICategoryUrl } from '@msdyn365-commerce/core-internal/dist/types/interfaces/ICategoryPathInterfaces';
import { DobbiesModal } from '.';
import { IDobbiesPopUpProps, Popup } from './pdp-overlay-popup';
import { updateLineDeliverySpecificationsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import { getDeliveryOptionsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { vSI_RemoveAllChargesAsync } from '../../../../vsi-add-charges-extension/DataActionExtension.g';
import { IBuyboxFindInStoreViewProps } from './get-buybox-find-in-store';
import { trackTHGAddToCart } from '../../../../utilities/thg-tracking';

export interface IDialogueStrings {
    goToCartText: string;
    continueShoppingText: string; // popupHeading
    headerItemOneText: string; // ageConfirmationHeaderText
    headerItemFormatText: string; // ageConfirmationText
    headerMessageText: string; // ageConfirmationFooterText
    freePriceText: string;
    originalPriceText: string;
    currentPriceText: string;
    ageRestrictionPopupHeading: string;
    ageRestrictionNote: string;
    ageConfirmation: string;
    ageVerificationDetails: string;
    cancelButtonLabel: string;
    buyBoxHeaderMessageText: string;
    buyBoxGoToCartText: string;
}

export interface IAddToCartComponentProps
    extends IComponentProps<{
        product: SimpleProduct;
        price?: ProductPrice;
        cart?: ICartState;
        customerInformation?: Customer;
        categoryPaths?: ICategoryUrl[];
    }> {
    className?: string;
    addToCartText: string;
    addToCartTextHd: string;

    outOfStockText: string;
    disabled?: boolean;
    quantity?: number;
    navigationUrl?: string;
    productAvailability?: ProductAvailableQuantity;
    getSelectedProduct?: Promise<SimpleProduct | null>;
    // VSI Customization to display pots products when plant product is added to bag
    plantsPotsProductsSlot?: React.ReactNode;
    potsProductCount?: number;
    // END
    imageSettings?: IImageSettings;
    gridSettings?: IGridSettings;

    isLoading?: boolean;
    isUpdatingDimension?: boolean;
    isAddServiceItemToCart?: boolean;
    /* Upgraded to 10.0.16 - START */
    isLoadingDeliveryOptions?: boolean;
    isUpdatingDeliveryOptions?: boolean;
    isAddEmailDeliveryItemToCart?: boolean;
    isPriceKeyedIn?: boolean;
    customPriceAmount?: number;
    isOrderQuantityLimitsFeatureEnabled?: boolean;
    /* Upgraded to 10.0.16 - END */
    dialogStrings?: IDialogueStrings;
    isAgeRestrictedProduct?: boolean;
    isFulfillmentProduct?: boolean;
    /* Upgraded to 10.0.16 - START */
    isCustomPriceSelected?: boolean;
    maximumKeyInPrice?: number;
    minimumKeyInPrice?: number;
    defaultMaximumKeyInPrice?: number;
    defaultMinimumKeyInPrice?: number;
    /* Upgraded to 10.0.16 - END */
    telemetryContent?: ITelemetryContent;
    unbxdCarousel: React.ReactNode[];

    onAdd?(result: ICartActionResult): void;
    onError?(result: IAddToCartFailureResult): void;
    changeUpdatingDimension?(isUpdatingDimension: boolean): void;
    /* Upgraded to 10.0.16 - START */
    changeUpdatingDeliveryOptions?(isUpdatingDeliveryOptions: boolean): void;
    /* Upgraded to 10.0.16 - END */
    onAddToCartClicked?(): void;
    deliveryOptions?: ProductDeliveryOptions;
    addToCartModalOpen?: boolean;
    setAddToCartModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
    isCNCSelected?: boolean;
    isHomeDelSelected?: boolean;
    findInStore?: IBuyboxFindInStoreViewProps;
    isCNCProductAvailable?: boolean;
    isMaxQuantityReached?: boolean;
}

export interface IAddToCartResult {
    addToCartButton: JSX.Element;
    addedToCartModal: JSX.Element;
    isAddedToCart: boolean;
}

export declare type ICartActionFailureReason =
    | 'EMPTYINPUT'
    | 'MISSINGDIMENSION'
    | 'OUTOFSTOCK'
    | 'CARTACTIONFAILED'
    | 'INVALIDCUSTOMAMOUNT'; /* Upgraded to 10.0.16 - INVALIDCUSTOMAMOUNT added */
export interface IAddToCartFailureResult {
    failureReason: ICartActionFailureReason;

    stockLeft?: number;
    cartActionResult?: ICartActionResult;
    missingDimensions?: ProductDimension[];
}

export interface IAddtoCartComponent extends IComponent<IAddToCartComponentProps> {
    onClick(): (event: React.MouseEvent<HTMLElement>, props: IAddToCartComponentProps) => void;
}

const removeAllCharges = async (cartState: ICartState, context: IActionContext) => {
    try {
        if (cartState.cart.Id) {
            await vSI_RemoveAllChargesAsync({ callerContext: context }, cartState.cart.Id);
        }
    } catch (e) {
        console.error('Error occured while removing charges', e);
    }
};

const onClick = async (
    _event: React.MouseEvent<HTMLElement>,
    props: IAddToCartComponentProps,
    setDisabled: (disabled: boolean) => void,
    openModal: (opened: boolean) => void,
    setAddedtoCart: React.Dispatch<React.SetStateAction<boolean>>,
    firstCase: boolean,
    toggleFirstCase: (firstCase: boolean) => void,
    setIsAgeRestrictionModalOpen: (disabled: boolean) => void,
    setChecked: (checked: boolean) => void,
    isAgeRestricted: boolean,
    setRelatedProductsModalOpen: React.Dispatch<React.SetStateAction<boolean>>
): Promise<void> => {
    setDisabled(true);
    let productToAdd = props.data.product;
    if (!(props.getSelectedProduct === undefined)) {
        productToAdd = (await props.getSelectedProduct) || props.data.product;
    }
    /* VSI Customization - START - 05/11/20 */
    /* If condition checks if product has age restriciton and 'Addtobasket' is clicked for the first time, open age restrcition popup */
    if (firstCase === true && isAgeRestricted === true) {
        toggleFirstCase(false);
        setIsAgeRestrictionModalOpen(true);
        setDisabled(false);
        setChecked(false);
        /* VSI Customization - END */
    } else {
        if (props.isCNCSelected && props.onAddToCartClicked) {
            props.onAddToCartClicked();
            setIsAgeRestrictionModalOpen(false);
            setDisabled(false);
            setChecked(false);
            return;
        }

        const cartError = addToCartError(props);

        if (cartError) {
            propogateError(props, cartError);
            return;
        }

        const cartState = await getCartState(props.context.actionContext);
        const {
            context: {
                app: { config }
            }
        } = props;

        const behavior = config && config.addToCartBehavior;

        await cartState
            .addProductToCart({
                product: productToAdd,
                count: props.quantity,
                availableQuantity: props.productAvailability && props.productAvailability.AvailableQuantity,
                additionalProperties: {
                    orderQuantityLimitsFeatureIsEnabled: props.isOrderQuantityLimitsFeatureEnabled
                } /* Upgraded to 10.0.16 */,
                enableStockCheck: props.context.app.config.enableStockCheck,
                /* Upgraded to 10.0.16 - START */
                isPriceKeyedIn: props.isPriceKeyedIn,
                customPrice: props.customPriceAmount,
                isAddEmailDeliveryItemToCart: props.isAddEmailDeliveryItemToCart
                /* Upgraded to 10.0.16 - END */
            })
            .then(async result => {
                if (result.status === 'SUCCESS') {
                    trackTHGAddToCart(props.context, productToAdd, props.quantity);
                    if (props.dialogStrings && behavior === 'showModal') {
                        const rctxt = props.context.request.query?.rctxt;
                        if (rctxt) {
                            const cartState = await getCartState(props.context.actionContext);
                            const cartLines = cartState.cart.CartLines;
                            const line = cartLines?.find(line => line.ProductId === productToAdd.RecordId);
                            if (cartLines && line && line.AttributeValues) {
                                const rctxtAttributeExists = line.AttributeValues.some(attribute => attribute.Name === 'RCTXT');
                                if (!rctxtAttributeExists) {
                                    const lineAttributes = [...line.AttributeValues!] as AttributeValue[];
                                    const rctxAttr = {
                                        '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
                                        Name: 'RCTXT',
                                        TextValue: rctxt,
                                        ExtensionProperties: []
                                    };
                                    lineAttributes.push(rctxAttr);

                                    await cartState.updateCartLines({
                                        updatedCartLinesObject: [{ ...line, AttributeValues: lineAttributes }]
                                    });
                                }
                            }
                        }
                        setDisabled(false);
                        const deliveryOptions = await getDeliveryOptionsAsync(
                            { callerContext: props.context.actionContext, queryResultSettings: {} },
                            [productToAdd.RecordId]
                        );

                        const deliveryMode = deliveryOptions[0].DeliveryOptions?.find(
                            option => option.Code !== 'Common MOD' && option.Code !== 'ClickCol'
                        );
                        const cartline = cartState.cart.CartLines?.find(
                            line => line.ProductId === productToAdd.RecordId && !line.FulfillmentStoreId
                        );

                        const defaultAddressString = props.context.request.app.config.defaultGuestCustomerAddressString;
                        if (!defaultAddressString) {
                            return;
                        }
                        const splittedDefaultAddress = defaultAddressString.split(',');
                        if (splittedDefaultAddress && splittedDefaultAddress.length < 3) {
                            return;
                        }

                        const street = splittedDefaultAddress[0];
                        const town = splittedDefaultAddress[1];
                        const zipcode = splittedDefaultAddress[2];
                        let defaultAddress: Address = {
                            Street: street,
                            ZipCode: zipcode,
                            City: town,
                            ThreeLetterISORegionName: 'GBR'
                        };
                        const deliverySpecification = [
                            {
                                LineId: cartline?.LineId,
                                DeliverySpecification: {
                                    DeliveryModeId: deliveryMode?.Code,
                                    DeliveryPreferenceTypeValue: 1,
                                    DeliveryAddress: defaultAddress
                                }
                            }
                        ];
                        await removeAllCharges(cartState, props.context.actionContext);
                        await updateLineDeliverySpecificationsAsync(
                            { callerContext: props.context.actionContext },
                            cartState.cart.Id,
                            deliverySpecification
                        );
                        await cartState.refreshCart({});
                        setIsAgeRestrictionModalOpen(false);
                        openModal(true);
                    } else if (props.navigationUrl && (behavior === undefined || behavior === 'nothing' || behavior === 'goToCart')) {
                        /* VSI Customization - START - 26/10/20 */
                        // Added addToCartBehavior === 'nothing' in if condition do the same when this settings is selected
                        /* When user clicks on 'ADD TO BAG' button,
                        - It would just ADD Product to CART and will not route to CART page.
                        - 'ADD TO BAG' button will not be disabled */
                        // window && window.location.assign(props.navigationUrl);
                        setDisabled(false);
                        setIsAgeRestrictionModalOpen(false);
                        setAddedtoCart(true);
                        // If product is of FulfillmentCategory, open related products popup
                        props?.potsProductCount && props?.potsProductCount > 0 && setRelatedProductsModalOpen(true);
                        /* VSI Customization - END */
                    }
                    propogateResult(props, result);
                } else {
                    propogateError(props, { failureReason: 'CARTACTIONFAILED', cartActionResult: result });
                    setDisabled(false);
                    setAddedtoCart(false);
                }
            });
    }
};

const AddToCartComponentActions = {
    onClick: onClick
};

/* VSI Customization - START - 06/11/20 */
/* This function renders 'Add to Basket' button.
popupAttributes decided whether to render it on PDP or on popup
if rendered on popup, it has additional attributes */
const _getAddtoBasketButton = (
    props: IAddToCartComponentProps,
    _disabled: boolean,
    onClickHandler: (event: React.MouseEvent<HTMLElement>) => Promise<void>,
    popupAttributes?:
        | {
              disabled: boolean;
              onClick(event: React.MouseEvent<HTMLElement>): Promise<void>;
          }
        | {
              disabled: boolean;
              onClick?: undefined;
          }
) => {
    const label = getLinkText(props);
    const payload = getPayloadObject(TelemetryEvent.AddToCart, props.telemetryContent!, label, '');
    const attributes = getTelemetryAttributes(props.telemetryContent!, payload);
    const _popupAttributes = popupAttributes ? popupAttributes : undefined;
    const isOutOfStock = shouldShowOutOfStock(props, false) && !props.isCNCProductAvailable;
    const [isHover, setIsHover] = useState(false);
    const isDisabled =
        props.disabled ||
        _disabled ||
        isIntermediateState(props) ||
        isOutOfStock ||
        (!props.isHomeDelSelected && !props.isCNCSelected) ||
        props.isMaxQuantityReached;

    return (
        <div
            className='row '
            onMouseLeave={e => {
                setIsHover(false);
            }}
            onMouseEnter={e => {
                if (!isDisabled) {
                    setIsHover(true);
                }
            }}
        >
            <button
                className={classnames('msc-add-to-cart', isOutOfStock ? 'product-delivery-out-of-stock' : '', props.className)}
                aria-label={label}
                {...attributes}
                onClick={onClickHandler}
                disabled={isDisabled}
                {..._popupAttributes}
                data-product-id={props.data.product.ItemId}
            >
                <div className={isHover ? 'msc-delivery__isHD active' : 'msc-delivery__isHD'} />

                {'Add to basket'}
            </button>
        </div>
    );
};
/* VSI Customization - START - 05/11/20
 ** Converted component to function to return addedToCart variable */
export const AddToCart = (props: IAddToCartComponentProps): IAddToCartResult => {
    const [disabled, setDisabled] = useState(false);
    // const [modalOpen, setModalOpen] = useState(false);
    /* VSI Customization - START - 26/10/20
    /* Following hook is used for age restriction popup.
    By default its value is true to handle first click and When user clicks 'Add to Cart' button for first time it sets to false for rest of the clicks */
    const [firstCase, toggleFirstCase] = useState(true);
    /* Following hook is used to open age restriction popup  */
    const [isAgeRestrictionModalOpen, setIsAgeRestrictionModalOpen] = useState(false);
    /* Following hook is used to toggle checkbox on age restriction popup */
    const [checked, setChecked] = useState(false);
    const [isAgeRestricted, setIsAgeRestricted] = useState(false);
    /* Following Hook is used to check if product has been added to Cart or not when user clicks on 'ADD TO CART' button. */
    const [addedtoCart, setAddedtoCart] = useState(false);
    /* Following hook is used to display related products list if product is of FulfillmentCategory */
    const [relatedProductsModalOpen, setRelatedProductsModalOpen] = useState(false);

    useEffect(() => {
        props.isAgeRestrictedProduct && setIsAgeRestricted(props.isAgeRestrictedProduct);
    });
    /* Added one more parameter 'setAddedtoCart' to onClick method*/
    const onClickHandler = async (event: React.MouseEvent<HTMLElement>) => {
        await AddToCartComponentActions.onClick(
            event,
            props,
            setDisabled,
            (value: boolean) => props.setAddToCartModalOpen && props.setAddToCartModalOpen(value),
            setAddedtoCart,
            firstCase,
            toggleFirstCase,
            setIsAgeRestrictionModalOpen,
            setChecked,
            isAgeRestricted,
            setRelatedProductsModalOpen
        );
    };

    /* VSI Customization - END */
    const priceComponent = props.data.price ? (
        <PriceComponent
            data={{ price: props.data.price }}
            context={props.context}
            id={props.id}
            typeName={props.typeName}
            freePriceText={props.dialogStrings && props.dialogStrings.freePriceText}
            originalPriceText={props.dialogStrings && props.dialogStrings.originalPriceText}
            currentPriceText={props.dialogStrings && props.dialogStrings.currentPriceText}
            quantity={props.quantity !== undefined ? props.quantity : 1}
        />
    ) : (
        ''
    );

    const popupProps: IDobbiesPopUpProps = {
        context: props.context,
        className: 'msc-add-to-cart',
        id: props.id,
        typeName: props.typeName,
        data: { product: props.data.product, price: props.data.price },
        dialogStrings: props.dialogStrings,
        imageSettings: props.imageSettings,
        gridSettings: props.context.request.gridSettings,
        productQuantity: props.quantity !== undefined ? props.quantity : 1,
        priceComponent: priceComponent,
        navigationUrl: props.navigationUrl,
        modalOpen: props.addToCartModalOpen,
        setModalOpen: (value: boolean) => props.setAddToCartModalOpen && props.setAddToCartModalOpen(value),
        cart: props.data.cart,
        customerInformation: props.data.customerInformation,
        unbxdCarousel: props.unbxdCarousel,
        categoryPaths: props.data.categoryPaths
    };

    const renderModalPopup = <Popup {...popupProps} />;
    const { dialogStrings, plantsPotsProductsSlot } = props;

    return {
        addToCartButton: (
            <>
                {/* VSI Customization - START - 26/10/20 */}
                {/* Moved AddtoBasket button into a function as it is used twice one on PDP page, second on age restriction popup */}
                {_getAddtoBasketButton(props, disabled, onClickHandler)}
                {/* DobbiesModal is used for popups i.e. age restriction popup */}
                {dialogStrings && (
                    <DobbiesModal
                        isOpen={isAgeRestrictionModalOpen}
                        modalTitle={addToCartModalTitle(setIsAgeRestrictionModalOpen, toggleFirstCase, setChecked, props, true)}
                        modalBodyElement={addToCartModalBody(checked, setChecked, props)}
                        modalFooter={addToCartModalFooter(
                            checked,
                            setChecked,
                            onClickHandler,
                            setIsAgeRestrictionModalOpen,
                            toggleFirstCase,
                            props,
                            disabled
                        )}
                        className='msc-dobbies-modal__add-to-cart-modal'
                    />
                )}
                <DobbiesModal
                    isOpen={!!(relatedProductsModalOpen && plantsPotsProductsSlot)}
                    modalTitle={addToCartModalTitle(setRelatedProductsModalOpen, toggleFirstCase, setChecked, props)}
                    modalBodyElement={displayRelatedProductsModule(props)}
                    modalFooter={<></>}
                    className='msc-dobbies-modal__add-to-cart-modal'
                />
            </>
        ),
        addedToCartModal: <>{renderModalPopup}</>,
        isAddedToCart: addedtoCart
    };
};

/* VSI Customization - START - 05/11/20 */

// Following function renders popup body including checkbox
const displayRelatedProductsModule = (props: IAddToCartComponentProps): JSX.Element | undefined => {
    const { plantsPotsProductsSlot } = props;
    if (plantsPotsProductsSlot === null || plantsPotsProductsSlot === undefined) {
        return undefined;
    }
    return React.cloneElement(plantsPotsProductsSlot as React.ReactElement);
};

// Following function renders popup header
const addToCartModalTitle = (
    setModalOpen: (disabled: boolean) => void,
    toggleFirstCase: (firstCase: boolean) => void,
    setChecked: (checked: boolean) => void,
    props: IAddToCartComponentProps,
    isAgeRestrictionPopup?: boolean // Check if to render modal for age restriction popup or for related products
): JSX.Element => {
    const closeModal = () => {
        setModalOpen(false);
        toggleFirstCase(true);
        setChecked(false);
    };

    const { dialogStrings } = props;

    return (
        <div className='age-restriction-header'>
            {dialogStrings && isAgeRestrictionPopup && (
                <h3 className='age-restriction-subheading'>{dialogStrings.ageRestrictionPopupHeading}</h3>
            )}
            {!isAgeRestrictionPopup && <h3>Related Products</h3>}
            <span role='button' onClick={closeModal} className='addtocart-cross msc-modal__close-button' />
        </div>
    );
};

// Following function renders popup body including checkbox
const addToCartModalBody = (checked: boolean, setChecked: (checked: boolean) => void, props: IAddToCartComponentProps): JSX.Element => {
    function toggleCheckBox(): void {
        setChecked(!checked);
    }

    const { dialogStrings } = props;

    return (
        <>
            {dialogStrings ? (
                <div className='age-restriction-body'>
                    <p className='age-restriction_note'>{dialogStrings.ageRestrictionNote}</p>
                    <label className='age-restriction_confirmation'>
                        <input
                            className='age-restriction_confirmation-checkbox'
                            type='checkbox'
                            onClick={toggleCheckBox}
                            aria-checked={checked}
                            checked={checked}
                        />
                        <span className='age-restriction_confirmation-text'>{dialogStrings.ageConfirmation}</span>
                    </label>
                    <p className='age-restriction_requirement'>{dialogStrings.ageVerificationDetails}</p>
                </div>
            ) : (
                undefined
            )}
        </>
    );
};
// Following function renders popup footer including 'Add to Basket' button
const addToCartModalFooter = (
    checked: boolean,
    setChecked: (checked: boolean) => void,
    onClickHandler: (event: React.MouseEvent<HTMLElement>) => Promise<void>,
    setAddtoCartModal: (disabled: boolean) => void,
    toggleFirstCase: (firstCase: boolean) => void,
    props: IAddToCartComponentProps,
    disabled: boolean
): JSX.Element => {
    const closeModal = () => {
        setAddtoCartModal(false);
        toggleFirstCase(true);
        setChecked(false);
    };
    // if checkbox is checked, only then 'Add to Basket' button will be enabled
    const popupAttributes = checked ? { disabled: false, onClick: onClickHandler } : { disabled: true };
    return (
        <div className='age-restriction-footer'>
            <button onClick={closeModal} className='msc-btn add-to-cart__age-restriction'>
                {props.dialogStrings && props.dialogStrings.cancelButtonLabel}
            </button>
            {_getAddtoBasketButton(props, disabled, onClickHandler, popupAttributes)}
        </div>
    );
};
/* VSI Customization - END - 06/11/20 */
// Set default props
AddToCart.defaultProps = {
    quantity: 1
};

const getLinkText = (props: IAddToCartComponentProps) => {
    return shouldShowOutOfStock(props, false) ? props.outOfStockText : props.addToCartTextHd;
};

const addToCartError = (props: IAddToCartComponentProps): IAddToCartFailureResult | undefined => {
    /* Upgraded to 10.0.16 - START */
    const {
        data,
        productAvailability,
        isCustomPriceSelected,
        customPriceAmount,
        maximumKeyInPrice,
        minimumKeyInPrice,
        defaultMaximumKeyInPrice = 100,
        defaultMinimumKeyInPrice = 10
    } = props;
    /* Upgraded to 10.0.16 - END */
    if (!data || !data.product.RecordId) {
        // No product exists, won't be able to add to cart
        return { failureReason: 'EMPTYINPUT' };
    }

    if (data.product.Dimensions) {
        const missingDimensions = data.product.Dimensions.filter(
            dimension => !(dimension.DimensionValue && dimension.DimensionValue.Value)
        );

        if (missingDimensions.length > 0) {
            // At least one dimension with no value exists on the product, won't be able to add to cart
            return { failureReason: 'MISSINGDIMENSION', missingDimensions: missingDimensions };
        }
    }

    if (shouldShowOutOfStock(props, true)) {
        const availableQuantity = (productAvailability && productAvailability.AvailableQuantity) || 0;
        const stockLeft = Math.max(availableQuantity, 0);

        return { failureReason: 'OUTOFSTOCK', stockLeft: stockLeft };
    }
    /* Upgraded to 10.0.16 - START */
    // When Custom price is selected, if there is no keyed-in price or keyed-in price is out of limit, should return error.
    if (
        isCustomPriceSelected &&
        (!customPriceAmount ||
            customPriceAmount > (maximumKeyInPrice || defaultMaximumKeyInPrice) ||
            customPriceAmount < (minimumKeyInPrice || defaultMinimumKeyInPrice))
    ) {
        return { failureReason: 'INVALIDCUSTOMAMOUNT' };
    }
    /* Upgraded to 10.0.16 - END */
    // Only allow adding to cart if not showing out of stock
    return undefined;
};

const shouldShowOutOfStock = (props: IAddToCartComponentProps, includeCurrentQuantity: boolean): boolean => {
    if (
        props.context.app.config.enableStockCheck === undefined ||
        props.context.app.config.enableStockCheck === false ||
        props.isLoading ||
        props.isUpdatingDimension ||
        props.isAddServiceItemToCart ||
        /* Upgraded to 10.0.16 - START */
        props.isLoadingDeliveryOptions ||
        props.isUpdatingDeliveryOptions
        /* Upgraded to 10.0.16 - END */
    ) {
        // Out of stock turn off, don't bother showing out of stock
        return false;
    }

    if (!props.data || !props.data.product.RecordId) {
        // No product exists, don't bother showing out of stock
        return false;
    }

    if (props.data.product.Dimensions) {
        if (props.data.product.Dimensions.find(dimension => !(dimension.DimensionValue && dimension.DimensionValue.Value))) {
            // At least one dimension with no value exists on the product, so also don't show out of stock
            return false;
        }
    }
    const includedQuantityNumber = includeCurrentQuantity && props.quantity ? props.quantity : 1;

    return props.productAvailability &&
        props.productAvailability.AvailableQuantity !== undefined &&
        props.productAvailability.AvailableQuantity >= includedQuantityNumber
        ? false
        : true;
};

const isIntermediateState = (props: IAddToCartComponentProps): boolean => {
    if (props.data.product.Dimensions) {
        if (props.data.product.Dimensions.find(dimension => !(dimension.DimensionValue && dimension.DimensionValue.Value))) {
            // At least one dimension with no value exists on the product, so also not in intermediate state
            return false;
        }
    }

    if (
        !props.isLoading &&
        !props.isUpdatingDimension &&
        /* Upgraded to 10.0.16 - START */
        !props.isLoadingDeliveryOptions &&
        !props.isUpdatingDeliveryOptions
        /* Upgraded to 10.0.16 - END */
    ) {
        return false;
    }

    return true;
};

const propogateResult = (props: IAddToCartComponentProps, result: ICartActionResult): void => {
    if (props.onAdd) {
        props.onAdd(result);
    }
};

const propogateError = (props: IAddToCartComponentProps, result: IAddToCartFailureResult): void => {
    if (props.onError) {
        props.onError(result);
    }
};

/* export const AddToCartComponent: React.FunctionComponent<IAddToCartComponentProps> = msdyn365Commerce.createComponent<IAddtoCartComponent>(
    'AddToCart',
    { component: AddToCart, ...AddToCartComponentActions }
); */

export default AddToCart;
